import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SegmentedControl,
  Switch,
  cn,
} from "@nexus/ui";
import {
  type KpiCatalogFormat,
  type KpiPolarity,
  type KpiColorToken,
} from "@nexus/contracts";
import { KPI_DECIMALS_MAX } from "@nexus/contracts";
import { COLUMN_FIELDS, defaultKpiColorTokenForName } from "@nexus/kpi-formula";
import { clampKpiDecimals, isValidKpiDecimals } from "../lib/kpi-decimals";
import { KPI_GROUPS, type KpiGroupDefinition, type KpiGroupId } from "../lib/kpi-groups";
import { KpiColorPicker } from "./KpiColorPicker";

const FORMAT_OPTIONS: KpiCatalogFormat[] = ["CURRENCY", "PERCENT", "NUMBER"];

export interface KpiSettingsDialogValue {
  displayName: string;
  formula: string;
  format: KpiCatalogFormat;
  polarity: KpiPolarity;
  decimals: number;
  colorToken: KpiColorToken;
  shareWithOrg: boolean;
  /** Set only when creating a user KPI. Org KPIs are not placed in drawer sections. */
  section?: KpiGroupId;
}

export interface KpiSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `create` vs `edit` changes the title; user-create also shows "Share with org". */
  mode: "create" | "edit";
  /** `user` adds the "Share with org" switch in create mode. */
  scope: "user" | "org";
  initial?: Partial<KpiSettingsDialogValue>;
  /**
   * Sections the user can place a new KPI in, in display order.
   * Omitted means every section. An empty list means every section was removed.
   */
  offeredSectionIds?: readonly KpiGroupId[];
  isSubmitting?: boolean;
  onSubmit: (value: KpiSettingsDialogValue) => void;
}

const EMPTY: KpiSettingsDialogValue = {
  displayName: "",
  formula: "spend",
  format: "NUMBER",
  polarity: "NEGATIVE",
  decimals: 2,
  colorToken: "chart-1",
  shareWithOrg: false,
};

function offeredKpiGroups(ids: readonly KpiGroupId[] | undefined): KpiGroupDefinition[] {
  const source = ids ?? KPI_GROUPS.map((group) => group.id);
  const groups: KpiGroupDefinition[] = [];
  for (const id of source) {
    const group = KPI_GROUPS.find((candidate) => candidate.id === id);
    if (!group || groups.some((existing) => existing.id === group.id)) continue;
    groups.push(group);
  }
  return groups;
}

function createDialogValue(
  initial: Partial<KpiSettingsDialogValue> | undefined,
  groups: readonly KpiGroupDefinition[],
): KpiSettingsDialogValue {
  const allowed = new Set(groups.map((group) => group.id));
  const section =
    initial?.section && allowed.has(initial.section) ? initial.section : groups[0]?.id;
  return { ...EMPTY, ...initial, section };
}

export function KpiSettingsDialog({
  open,
  onOpenChange,
  mode,
  scope,
  initial,
  offeredSectionIds,
  isSubmitting = false,
  onSubmit,
}: KpiSettingsDialogProps) {
  const { t } = useTranslation();
  const offeredSections = offeredKpiGroups(offeredSectionIds);
  const [value, setValue] = useState<KpiSettingsDialogValue>(() =>
    createDialogValue(initial, offeredSections),
  );
  const [colorTouched, setColorTouched] = useState(false);

  // Re-seed whenever the dialog opens so an edit never shows the previous row's
  // values, and a create always starts clean after a submit.
  useEffect(() => {
    if (open) {
      setValue(createDialogValue(initial, offeredKpiGroups(offeredSectionIds)));
      setColorTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A layout that arrives after the dialog opens can drop the seeded section.
  // Keep a name the user already typed; only move the section when it is gone.
  const offeredKey = offeredSections.map((group) => group.id).join(",");
  useEffect(() => {
    if (!open || mode !== "create" || scope !== "user") return;
    setValue((current) => {
      const groups = offeredKpiGroups(offeredSectionIds);
      const allowed = new Set(groups.map((group) => group.id));
      if (current.section && allowed.has(current.section)) return current;
      const section = groups[0]?.id;
      if (current.section === section) return current;
      return { ...current, section };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, scope, offeredKey]);

  function patch(next: Partial<KpiSettingsDialogValue>) {
    setValue((current) => ({ ...current, ...next }));
  }

  const isCreate = mode === "create";
  const displayNameMissing = value.displayName.trim() === "";
  const sectionOffered =
    !(isCreate && scope === "user") ||
    offeredSections.some((group) => group.id === value.section);
  const canSubmit =
    !displayNameMissing &&
    value.formula.trim() !== "" &&
    isValidKpiDecimals(value.decimals) &&
    !isSubmitting &&
    sectionOffered;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isCreate
              ? t("kpiSettings.createTitle", "Add a KPI")
              : t("kpiSettings.editTitle", "Edit KPI")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "kpiSettings.formulaHint",
              "Formulas may only reference the listed column tokens. They compile to BigQuery for sorting.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            {t("kpiSettings.displayName", "Display name")}
            <input
              className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none focus:border-ring"
              value={value.displayName}
              onChange={(event) => {
                const displayName = event.target.value;
                const trimmed = displayName.trim();
                patch({
                  displayName,
                  ...(isCreate && !colorTouched && trimmed
                    ? { colorToken: defaultKpiColorTokenForName(trimmed) }
                    : {}),
                });
              }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            {t("kpiSettings.formula", "Formula")}
            <input
              className="h-8 rounded-md border border-border bg-background px-2 font-mono text-sm text-foreground outline-none focus:border-ring"
              value={value.formula}
              onChange={(event) => patch({ formula: event.target.value })}
            />
          </label>

          <div className="flex flex-wrap gap-1">
            {COLUMN_FIELDS.map((token) => (
              <button
                key={token}
                type="button"
                className="rounded-md border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() =>
                  patch({ formula: value.formula ? `${value.formula} ${token}` : token })
                }
              >
                {token}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-muted-foreground">
              {t("kpiSettings.format", "Format")}
              <select
                className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                value={value.format}
                onChange={(event) => patch({ format: event.target.value as KpiCatalogFormat })}
              >
                {FORMAT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`iam.orgSettings.kpis.format.${option.toLowerCase()}`, option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex w-20 flex-col gap-1 text-xs font-medium text-muted-foreground">
              {t("kpiSettings.decimals", "Decimals")}
              <input
                type="number"
                min={0}
                max={KPI_DECIMALS_MAX}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                value={value.decimals}
                onChange={(event) =>
                  patch({ decimals: clampKpiDecimals(Number(event.target.value)) })
                }
              />
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t("kpiSettings.polarity", "Polarity")}
            </span>
            <SegmentedControl
              value={value.polarity}
              onValueChange={(next) => patch({ polarity: next as KpiPolarity })}
              ariaLabel={t("kpiSettings.polarity", "Polarity")}
              options={[
                { value: "NEGATIVE", label: t("kpiSettings.polarityNegative", "Above zero is negative") },
                { value: "POSITIVE", label: t("kpiSettings.polarityPositive", "Above zero is positive") },
              ]}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t("kpiSettings.color", "Color")}
            </span>
            <KpiColorPicker
              value={value.colorToken}
              onChange={(colorToken) => {
                setColorTouched(true);
                patch({ colorToken });
              }}
            />
          </div>

          {isCreate && scope === "user" && (
            <label
              htmlFor="kpi-settings-section"
              className="flex flex-col gap-1 text-xs font-medium text-muted-foreground"
            >
              {t("kpiSettings.groups.section", "Section")}
              <select
                id="kpi-settings-section"
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
                value={value.section ?? ""}
                onChange={(event) => patch({ section: event.target.value as KpiGroupId })}
              >
                {offeredSections.map((group) => (
                  <option key={group.id} value={group.id}>
                    {t(group.labelKey, group.defaultLabel)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {isCreate && scope === "user" && (
            <div className={cn("flex items-start justify-between gap-3 rounded-md border border-border p-2.5")}>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-foreground">
                  {t("kpiSettings.shareWithOrg", "Share with org")}
                </span>
                <span className="text-[11px] leading-snug text-muted-foreground">
                  {t(
                    "kpiSettings.shareWithOrgHint",
                    "Adds this KPI to Org KPIs so others can opt in. Numbers still appear in the shared tenant query either way.",
                  )}
                </span>
              </div>
              <Switch
                checked={value.shareWithOrg}
                onCheckedChange={(checked) => patch({ shareWithOrg: checked })}
                aria-label={t("kpiSettings.shareWithOrg", "Share with org")}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                displayName: value.displayName.trim(),
                formula: value.formula.trim(),
                format: value.format,
                polarity: value.polarity,
                decimals: clampKpiDecimals(value.decimals),
                colorToken: value.colorToken,
                shareWithOrg: value.shareWithOrg,
                ...(isCreate && scope === "user" ? { section: value.section } : {}),
              })
            }
          >
            {isSubmitting
              ? t("kpiSettings.saving", "Saving…")
              : t("kpiSettings.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
